import {
  MuiTelInput,
  MuiTelInputCountry,
  MuiTelInputInfo,
  MuiTelInputProps,
} from 'mui-tel-input';
import React, { useLayoutEffect, useRef } from 'react';

export type PhoneInputProps = MuiTelInputProps;

type InputSnapshot = {
  cursor: number;
  inputType: string;
  value: string;
};

function toNationalDisplay(
  fullValue: string,
  callingCode: string | null
): string {
  if (!callingCode) {
    return fullValue;
  }

  const prefix = `+${callingCode}`;

  if (fullValue.startsWith(prefix)) {
    return fullValue.slice(prefix.length).trimStart();
  }

  return fullValue;
}

function nextCursorPosition(
  prev: string,
  cursor: number,
  next: string,
  inputType: string
): number {
  const digitsBefore = prev.slice(0, cursor).replace(/\D/g, '').length;
  const prevDigits = prev.replace(/\D/g, '').length;
  const nextDigits = next.replace(/\D/g, '').length;

  let target = digitsBefore;

  if (nextDigits > prevDigits) {
    target += 1;
  } else if (nextDigits < prevDigits) {
    if (inputType === 'deleteContentForward') {
      target = digitsBefore;
    } else {
      target = Math.max(0, digitsBefore - 1);
    }
  }

  if (target === 0) {
    return 0;
  }

  let seen = 0;
  for (let i = 0; i < next.length; i++) {
    if (/\d/.test(next[i]) && ++seen >= target) {
      return i + 1;
    }
  }

  return next.length;
}

function inputTypeFromKey(key: string): string {
  if (key === 'Backspace') {
    return 'deleteContentBackward';
  }

  if (key === 'Delete') {
    return 'deleteContentForward';
  }

  return 'insertText';
}

function assignInputRef(
  element: HTMLInputElement | null,
  internalRef: React.MutableRefObject<HTMLInputElement | null>,
  externalRef?: React.Ref<HTMLInputElement>
) {
  internalRef.current = element;

  if (typeof externalRef === 'function') {
    externalRef(element);
    return;
  }

  if (externalRef && typeof externalRef === 'object') {
    externalRef.current = element;
  }
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onKeyDown,
  onPaste,
  defaultCountry = 'US',
  inputRef: externalInputRef,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorRef = useRef<number | null>(null);
  const beforeInput = useRef<InputSnapshot | null>(null);

  const setInputRef = (element: HTMLInputElement | null) => {
    assignInputRef(element, inputRef, externalInputRef);
  };

  const captureInput = (inputType: string) => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    beforeInput.current = {
      value: input.value,
      cursor: input.selectionStart ?? 0,
      inputType,
    };
  };

  useLayoutEffect(() => {
    const input = inputRef.current;

    if (input && cursorRef.current !== null) {
      const pos = cursorRef.current;
      cursorRef.current = null;
      input.setSelectionRange(pos, pos);
    }
  });

  const handleChange = (newValue: string, info: MuiTelInputInfo) => {
    const prior = beforeInput.current;
    beforeInput.current = null;

    if (prior) {
      cursorRef.current = nextCursorPosition(
        prior.value,
        prior.cursor,
        toNationalDisplay(newValue, info.countryCallingCode),
        prior.inputType
      );
    }

    onChange?.(newValue, info);
  };

  return (
    <MuiTelInput
      {...props}
      value={value || ''}
      onChange={handleChange}
      onKeyDown={(event) => {
        if (
          event.key === 'Backspace' ||
          event.key === 'Delete' ||
          (event.key.length === 1 && !event.ctrlKey && !event.metaKey)
        ) {
          captureInput(inputTypeFromKey(event.key));
        }
        onKeyDown?.(event);
      }}
      onPaste={() => {
        captureInput('insertFromPaste');
      }}
      defaultCountry={defaultCountry as MuiTelInputCountry}
      focusOnSelectCountry={false}
      forceCallingCode={true}
      inputRef={setInputRef}
    />
  );
};
